import { all, call, delay, fork, put, takeLatest } from 'redux-saga/effects';
import ApiConstants from 'src/common/helpers/ApiConstants';
import * as requests from 'src/common/helpers/HTTPRequest';
import { toastError, toastSuccess } from 'src/common/helpers/Notifications';
import TextConstants from 'src/common/helpers/TextConstants';
import { API_KEYS_ACTION } from './actions';

function* getApiKeys({ payload }) {
  try {
    const { includeInvalid } = payload || {};
    let url = `${ApiConstants.BASE_URL}${ApiConstants.API_KEYS}`;

    // Build query parameters
    if (includeInvalid) {
      url += '?includeInvalid=true';
    }

    const apiRes = yield call(requests.getRequest, url, {});

    // Handle response - could be apiRes.data.apiKeys, apiRes.apiKeys, or apiRes directly
    const apiKeys = apiRes?.data?.apiKeys || apiRes?.apiKeys || [];

    yield put({
      type: API_KEYS_ACTION.GET_API_KEYS_SUCCESS,
      payload: { apiKeys, includeInvalid: !!includeInvalid },
    });
  } catch (e) {
    console.log(e);
    toastError(e.message);
    yield put({
      type: API_KEYS_ACTION.GET_API_KEYS_ERROR,
      payload: e.message,
    });
  }
}

function* createApiKey({ payload }) {
  try {
    const url = `${ApiConstants.BASE_URL}${ApiConstants.API_KEYS}`;
    const apiRes = yield call(requests.postRequest, url, payload, true);

    // Handle response - could be apiRes.data or apiRes directly depending on HTTPRequest wrapper
    const responseData = apiRes?.data || apiRes;

    yield put({
      type: API_KEYS_ACTION.CREATE_API_KEY_SUCCESS,
      payload: responseData,
    });

    toastSuccess(
      TextConstants.Common.AddedSuccessMessage.replace(
        '{item}',
        TextConstants.ApiKeysManagement.ApiKey
      )
    );
  } catch (e) {
    console.log(e);
    toastError(e.message);
    yield put({
      type: API_KEYS_ACTION.CREATE_API_KEY_ERROR,
      payload: e.message,
    });
  } finally {
    yield delay(500);
    yield put({ type: API_KEYS_ACTION.CREATE_API_KEY_CLEAN_STATE });
  }
}

function* updateApiKey({ payload }) {
  try {
    const { apiKey, ...updateData } = payload;
    const url = `${ApiConstants.BASE_URL}${ApiConstants.API_KEYS}/${encodeURIComponent(apiKey)}`;
    const apiRes = yield call(requests.patchRequest, url, updateData, true);

    // Handle response - could be apiRes.data or apiRes directly
    const responseData = apiRes?.data || apiRes;

    yield put({
      type: API_KEYS_ACTION.UPDATE_API_KEY_SUCCESS,
      payload: responseData,
    });

    toastSuccess(
      TextConstants.Common.EditedSuccessMessage.replace(
        '{item}',
        TextConstants.ApiKeysManagement.ApiKey
      )
    );
  } catch (e) {
    console.log(e);
    toastError(e.message);
    yield put({
      type: API_KEYS_ACTION.UPDATE_API_KEY_ERROR,
      payload: e.message,
    });
  } finally {
    yield delay(500);
    yield put({ type: API_KEYS_ACTION.UPDATE_API_KEY_CLEAN_STATE });
  }
}

function* revokeApiKey({ payload }) {
  try {
    const { apiKey } = payload;
    const url = `${ApiConstants.BASE_URL}${ApiConstants.API_KEYS}/${encodeURIComponent(apiKey)}/revoke`;
    const apiRes = yield call(requests.postRequest, url, {}, true);

    // Handle response - could be apiRes.data or apiRes directly
    const responseData = apiRes?.data || apiRes;

    yield put({
      type: API_KEYS_ACTION.REVOKE_API_KEY_SUCCESS,
      payload: responseData,
    });

    toastSuccess(TextConstants.ApiKeysManagement.RevokeSuccess);
  } catch (e) {
    console.log(e);
    toastError(e.message);
    yield put({
      type: API_KEYS_ACTION.REVOKE_API_KEY_ERROR,
      payload: e.message,
    });
  } finally {
    yield delay(500);
    yield put({ type: API_KEYS_ACTION.REVOKE_API_KEY_CLEAN_STATE });
  }
}

function* deleteApiKeySaga({ payload }) {
  try {
    const { apiKey } = payload;
    const url = `${ApiConstants.BASE_URL}${ApiConstants.API_KEYS}/${encodeURIComponent(apiKey)}`;
    const apiRes = yield call(requests.deleteRequest, url, {}, true);

    yield put({
      type: API_KEYS_ACTION.DELETE_API_KEY_SUCCESS,
      payload: apiRes?.data || { apiKey },
    });

    toastSuccess(
      TextConstants.Common.DeletedSuccessMessage.replace(
        '{item}',
        TextConstants.ApiKeysManagement.ApiKey
      )
    );
  } catch (e) {
    console.log(e);
    toastError(e.message);
    yield put({
      type: API_KEYS_ACTION.DELETE_API_KEY_ERROR,
      payload: e.message,
    });
  } finally {
    yield delay(500);
    yield put({ type: API_KEYS_ACTION.DELETE_API_KEY_CLEAN_STATE });
  }
}

export function* ApiKeysManagementSaga() {
  yield takeLatest(API_KEYS_ACTION.GET_API_KEYS, getApiKeys);
  yield takeLatest(API_KEYS_ACTION.CREATE_API_KEY, createApiKey);
  yield takeLatest(API_KEYS_ACTION.UPDATE_API_KEY, updateApiKey);
  yield takeLatest(API_KEYS_ACTION.REVOKE_API_KEY, revokeApiKey);
  yield takeLatest(API_KEYS_ACTION.DELETE_API_KEY, deleteApiKeySaga);
}

export default function* rootSaga() {
  yield all([fork(ApiKeysManagementSaga)]);
}
